import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTenantFormat } from '../hooks/useTenantFormat';
import { useToast } from '../hooks/useToast';
import { api } from '../lib/api';
import { Users, ClipboardList, IndianRupee, TrendingUp, RefreshCw, AlertCircle, DollarSign, TrendingDown, Package } from 'lucide-react';

/**
 * Self-contained School Insights panel.
 * Shared between SchoolDashboard (standalone page) and Home (inline for school/coaching tenants).
 * Props:
 *   showHeader — render the "School Dashboard" title + refresh button
 */
export default function SchoolInsightsPanel({ showHeader = false }) {
  const { token } = useAuth();
  const { formatMoney } = useTenantFormat();
  const toast = useToast();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const r = await api.schoolInsights(token);
      setData(r);
    } catch (e) {
      if (e?.status === 403) { setError('business_type_required'); }
      else { toast.show(e?.message || 'Failed to load insights', 'error'); }
    } finally { setLoading(false); }
  }, [token, toast]);

  useEffect(() => { load(); }, [load]);

  if (error === 'business_type_required') {
    return (
      <div className="max-w-md mx-auto bg-white border border-gray-200 rounded-2xl shadow-sm p-10 text-center" data-testid="school-biz-type-notice">
        <AlertCircle size={40} className="text-amber-500 mx-auto mb-4" />
        <h2 className="text-gray-900 font-extrabold text-xl mb-3">School Business Type Required</h2>
        <p className="text-gray-500 text-sm mb-6">Switch to School business type in Profile to see insights.</p>
        <button onClick={() => navigate('/profile')} className="px-6 py-2.5 rounded-xl bg-indigo-600 text-white font-bold text-sm hover:bg-indigo-700 transition-colors">Go to Profile</button>
      </div>
    );
  }

  const k = data?.kpis || {};
  const collected = k.total_collected_this_month || 0;
  const outstanding = k.total_outstanding || 0;
  const billedTotal = collected + outstanding;
  const collPct = billedTotal > 0 ? Math.round((collected / billedTotal) * 100) : 0;
  const now = new Date();
  const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  return (
    <div data-testid="school-insights-panel">
      {showHeader && (
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-extrabold text-gray-900" data-testid="school-dashboard-title">School Dashboard</h1>
            <p className="text-gray-500 text-sm mt-1">{data?.period ? `Period: ${data.period}` : ''}</p>
          </div>
          <button onClick={load} disabled={loading} data-testid="school-dashboard-refresh"
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      )}

      {loading && !data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {[1,2,3,4].map(i => <div key={i} className="h-36 bg-gray-100 rounded-2xl animate-pulse" />)}
        </div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-8">
            <KpiCard title="Enrolled Students" value={k.total_enrolled_students ?? 0} icon={Users} accent="blue" testId="kpi-enrolled" onClick={() => navigate('/school/students')} />
            <KpiCard title="Active Fee Plans" value={k.active_fee_plans_count ?? 0} icon={ClipboardList} accent="purple" testId="kpi-fee-plans" onClick={() => navigate('/school/fee-plans')} />
            <KpiCard title="Billed This Month" value={formatMoney(k.total_billed_this_month ?? 0)} icon={IndianRupee} accent="amber" testId="kpi-billed" onClick={() => navigate(`/school/fees?period=${currentPeriod}`)} isCurrency />
            <div onClick={() => navigate('/school/fees?payment_status=unpaid')} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 cursor-pointer hover:shadow-md transition-shadow" data-testid="kpi-collected-outstanding">
              <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-3">Collected / Outstanding</p>
              <div className="flex items-end gap-4">
                <div>
                  <p className="text-emerald-600 text-xl font-extrabold" data-testid="kpi-collected-val">{formatMoney(collected)}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Collected</p>
                </div>
                <div className="text-gray-300 text-lg font-light">/</div>
                <div>
                  <p className="text-red-500 text-xl font-extrabold" data-testid="kpi-outstanding-val">{formatMoney(outstanding)}</p>
                  <p className="text-[11px] text-gray-400 mt-0.5">Outstanding</p>
                </div>
              </div>
            </div>
          </div>

          {/* Collection Progress */}
          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 mb-8" data-testid="collection-progress">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-gray-700">Collection Progress</p>
              <span className="text-sm font-bold text-indigo-600" data-testid="collection-pct">{collPct}% collected this month</span>
            </div>
            <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-500" style={{ width: `${collPct}%` }} />
            </div>
            <div className="flex justify-between mt-2 text-[11px] text-gray-400">
              <span>{formatMoney(collected)} collected</span>
              <span>{formatMoney(outstanding)} outstanding</span>
            </div>
          </div>

          {/* Monthly Financials */}
          <MonthlyFinancials mf={data?.monthly_financials} formatMoney={formatMoney} />

          {/* Revenue Breakdown */}
          <RevenueBreakdown rb={data?.revenue_breakdown} formatMoney={formatMoney} />

          {/* Top Products */}
          <TopProducts products={data?.top_products} formatMoney={formatMoney} navigate={navigate} />
        </>
      )}
    </div>
  );
}

function MonthlyFinancials({ mf, formatMoney }) {
  const fin = mf || {};
  const net = fin.net ?? 0;
  const isPositive = net >= 0;
  return (
    <div className="mb-8" data-testid="monthly-financials">
      <h2 className="text-lg font-extrabold text-gray-900 mb-4">Monthly Financials</h2>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="fin-revenue">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-xl bg-blue-50 flex items-center justify-center border border-blue-100"><DollarSign size={16} className="text-blue-600" /></div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Sales Revenue</p>
          </div>
          <p className="text-xl font-extrabold text-gray-900" data-testid="fin-revenue-val">{formatMoney(fin.total_sales_revenue ?? 0)}</p>
          <p className="text-[11px] text-gray-400 mt-1">This Month</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="fin-expenses">
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-xl bg-red-50 flex items-center justify-center border border-red-100"><TrendingDown size={16} className="text-red-500" /></div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Expenses</p>
          </div>
          <p className="text-xl font-extrabold text-gray-900" data-testid="fin-expenses-val">{formatMoney(fin.total_expenses ?? 0)}</p>
          <p className="text-[11px] text-gray-400 mt-1">This Month</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5" data-testid="fin-net">
          <div className="flex items-center gap-2.5 mb-2">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center border ${isPositive ? 'bg-emerald-50 border-emerald-100' : 'bg-red-50 border-red-100'}`}>
              <TrendingUp size={16} className={isPositive ? 'text-emerald-600' : 'text-red-500'} />
            </div>
            <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">Net</p>
          </div>
          <p className={`text-xl font-extrabold ${isPositive ? 'text-emerald-600' : 'text-red-500'}`} data-testid="fin-net-val">{formatMoney(net)}</p>
          <p className="text-[11px] text-gray-400 mt-1">This Month</p>
        </div>
      </div>
    </div>
  );
}

function RevenueBreakdown({ rb, formatMoney }) {
  const rev = rb || {};
  const feeAmt = rev.fee_collection ?? 0;
  const otherAmt = rev.other_sales ?? 0;
  const feePct = rev.fee_pct ?? 0;
  const otherPct = rev.other_pct ?? 0;
  const hasData = feeAmt > 0 || otherAmt > 0;

  return (
    <div className="mb-8" data-testid="revenue-breakdown">
      <h2 className="text-lg font-extrabold text-gray-900 mb-4">Revenue Breakdown</h2>
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5">
        {!hasData ? (
          <p className="text-gray-400 text-sm text-center py-4" data-testid="revenue-empty">No sales recorded this month.</p>
        ) : (
          <>
            <div className="w-full h-8 bg-gray-100 rounded-full overflow-hidden flex" data-testid="revenue-bar">
              {feePct > 0 && (
                <div className="h-full bg-gradient-to-r from-purple-500 to-purple-600 transition-all duration-500" style={{ width: `${feePct}%` }} />
              )}
              {otherPct > 0 && (
                <div className="h-full bg-gradient-to-r from-teal-400 to-teal-500 transition-all duration-500" style={{ width: `${otherPct}%` }} />
              )}
            </div>
            <div className="flex items-center gap-6 mt-4">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-purple-500 flex-shrink-0" />
                <span className="text-sm text-gray-600" data-testid="revenue-fee-label">Fee: {formatMoney(feeAmt)} ({feePct}%)</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-teal-500 flex-shrink-0" />
                <span className="text-sm text-gray-600" data-testid="revenue-other-label">Other: {formatMoney(otherAmt)} ({otherPct}%)</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function TopProducts({ products, formatMoney, navigate }) {
  const items = products || [];
  return (
    <div className="mb-8" data-testid="top-products">
      <h2 className="text-lg font-extrabold text-gray-900 mb-4">Top Products Sold This Month</h2>
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        {items.length === 0 ? (
          <div className="py-10 text-center">
            <Package size={36} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-400 text-sm" data-testid="top-products-empty">No products sold this month.</p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="top-products-table">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/80">
                <th className="text-center px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider w-14">#</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Product</th>
                <th className="text-left px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Unit</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Qty Sold</th>
                <th className="text-right px-4 py-3 text-xs text-gray-500 font-bold uppercase tracking-wider">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p, i) => (
                <tr key={p.product_id || i}
                  className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors cursor-pointer"
                  data-testid={`top-product-${i}`}
                  onClick={() => navigate(`/inventory?q=${encodeURIComponent(p.product_name)}`)}>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg text-xs font-extrabold
                      ${i === 0 ? 'bg-amber-100 text-amber-700' : i === 1 ? 'bg-gray-200 text-gray-600' : i === 2 ? 'bg-orange-100 text-orange-600' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{p.product_name}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs uppercase">{p.unit || '—'}</td>
                  <td className="px-4 py-3 text-right font-semibold text-gray-700 tabular-nums">{p.qty_sold}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900 tabular-nums">{formatMoney(p.revenue)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function KpiCard({ title, value, icon: Icon, accent, testId, onClick, isCurrency }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600 border-blue-100',
    purple: 'bg-purple-50 text-purple-600 border-purple-100',
    amber: 'bg-amber-50 text-amber-600 border-amber-100',
  };
  return (
    <div onClick={onClick} className="bg-white border border-gray-200 rounded-2xl shadow-sm p-5 cursor-pointer hover:shadow-md transition-shadow" data-testid={testId}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center border ${colors[accent] || colors.blue}`}>
          <Icon size={18} />
        </div>
        <p className="text-xs text-gray-500 uppercase font-bold tracking-wider">{title}</p>
      </div>
      <p className={`${isCurrency ? 'text-xl' : 'text-2xl'} font-extrabold text-gray-900`} data-testid={`${testId}-val`}>{value}</p>
    </div>
  );
}
